import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import api from '@/api';
// components
import Button, { IButtonTypes } from '@/components/ui/buttons/Button';
// images
import {
  GoogleIcon,
  GoogleFillIcon,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
// styles
import styles from './styles.module.scss';
// types
import { ILoginType } from '@/components/landing-page-templates/ConfigLandingPage/types';
import { IAccountRegistrationEnum } from '@/models/account/types';
// configurations
import { GOOGLE_OAUTH_CLIENT_ID } from '@/config';

const GoogleLoginWrapper = ({
  updateLoginMethod,
  updateLastLoginMethod,
  existingLoginMethod,
  setNotificationMode,
  signInButton = false,
  isAttendeeRegistration,
  setCheckRegistrationExists,
  scriptLoaded,
  organizationId,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useParams<{ eventId: string }>();

  // Create a boolean to disable/enable the Google Login Button
  const [googleLoginDisabled, setGoogleLoginDisabled] = useState(false);

  // Handle errors with the Google Login Button
  const responseGoogleError = response => {
    // Log the error; we are aware of cookie or whitelist origins as being two problems which can prevent the google login
    console.group('Google Login error');
    console.error(response.error);
    console.error(response.details);
    console.groupEnd();

    // NOTE - this can be either from Cookies being disabled or invalid origin configuration
    //  e.g. response.details: "Not a valid origin for the client: https://testing.zuddl.com has not been whitelisted for client ID... Please go to https://console.developers.google.com/ and whitelist this origin for your project's client ID."
    //  e.g. response.details: "Cookies are not enabled in current environment."
    if (
      response.error === 'idpiframe_initialization_failed' &&
      response.error_description ===
        'Cookies are not enabled in current environment.'
    ) {
      dispatch({
        type: 'global/addDangerToast',
        payload: {
          description:
            'Google Login is disabled in private / incognito windows.',
        },
      });
      setGoogleLoginDisabled(true);
    }
  };

  // Handle a successful Google Login Button response
  const responseGoogle = response => {
    return api.account
      .googleLoginV2(
        response.code,
        eventId,
        organizationId,
        isAttendeeRegistration,
      )
      .then(({ data }) => {
        dispatch({ type: 'account/setAccount' });
        dispatch({ type: 'account/setUserData', payload: { data } });
        dispatch({
          type: 'account/setRegistrationStep',
          payload: { registrationStep: IAccountRegistrationEnum.COMPLETE },
        });
      })
      .catch(e => {
        // NOTE - checking for the 500 error with the custom message
        //  "INVITE_ONLY_NO_ACCESS" for this case. Was trying to return 403
        //  "Forbidden" but it was not working on the production environment.
        if (
          e.response?.status === 500 &&
          e.response?.data?.message === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.NO_ACCESS },
          });
        } else if (
          e.response?.status === 401 &&
          e.response?.data?.message === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.NO_ACCESS },
          });
        } else if (
          e?.response?.status === 406 &&
          e?.response?.data.message === 'NOT_ACCEPTABLE'
        ) {
          setCheckRegistrationExists(false);
          dispatch({
            type: 'global/addDangerToast',
            payload: { description: e?.response?.data.description },
          });
        }
        // Event Level login
        // 406 "Not Acceptable" error means they are using the wrong auth mode/type
        else if (
          e.response?.status === 406 &&
          updateLoginMethod &&
          updateLastLoginMethod
        ) {
          // Set the Login method to Google and set the last login method from the API response (406 means the auth methods don't match)
          updateLoginMethod(ILoginType.GOOGLE);
          updateLastLoginMethod(e.response.data);
        }
        // App level login
        else if (e.response?.status === 406) {
          dispatch({
            type: 'global/addDangerToast',
            payload: {
              description: `You need to sign in with ${e.response.data}`,
            },
          });
        } else if (
          e?.response?.status === 401 &&
          e?.response?.data === 'Not Authorized for this event'
        ) {
          setNotificationMode(true);
          dispatch({
            type: 'global/addDangerToast',
            payload: {
              description: `You have been removed from the event. Please contact the
              organiser to resolve the issue.`,
            },
          });
        } else {
          // Some error occurred while logging in with google; user is still at the login screen so no redirect required.
          dispatch({
            type: 'global/addDangerToast',
            payload: { description: 'Unable to sign in with Google.' },
          });
        }
      });
  };

  const googleImageWithText = (
    <div>
      {existingLoginMethod ? (
        <div className={styles.buttonTextContainer}>
          <GoogleIcon size={19} />
          <div
            data-testid="sign-in-with-google"
            className={styles.googleButtonText}
          >
            {signInButton ? `Sign in with Google` : `Continue with Google`}
          </div>
        </div>
      ) : (
        <div>
          <GoogleIcon />
        </div>
      )}
    </div>
  );

  const login = useGoogleLogin({
    onSuccess: responseGoogle,
    onError: responseGoogleError,
    flow: 'auth-code',
  });

  const handleLoginClick = () => {
    if (!scriptLoaded) {
      console.log('Error loading Google login sdk');
      return;
    }
    login();
  };

  return (
    <Button
      onClick={handleLoginClick}
      styleClass={
        existingLoginMethod
          ? IButtonTypes.SOCIAL_LOGIN_POPUP_BUTTON
          : IButtonTypes.SOCIAL_LOGIN_POPUP_ICON
      }
      type="button"
      disabled={googleLoginDisabled || !scriptLoaded}
    >
      {googleImageWithText}
    </Button>
  );
};

export function GoogleLoginButton({
  updateLoginMethod,
  updateLastLoginMethod,
  existingLoginMethod,
  setNotificationMode,
  signInButton = false,
  isAttendeeRegistration,
  setCheckRegistrationExists,
  organizationId,
}) {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  return (
    <GoogleOAuthProvider
      clientId={GOOGLE_OAUTH_CLIENT_ID}
      onScriptLoadSuccess={() => setScriptLoaded(true)}
    >
      <GoogleLoginWrapper
        updateLoginMethod={updateLoginMethod}
        updateLastLoginMethod={updateLastLoginMethod}
        existingLoginMethod={existingLoginMethod}
        setNotificationMode={setNotificationMode}
        signInButton={signInButton}
        isAttendeeRegistration={isAttendeeRegistration}
        setCheckRegistrationExists={setCheckRegistrationExists}
        scriptLoaded={scriptLoaded}
        organizationId={organizationId}
      />
    </GoogleOAuthProvider>
  );
}

export default GoogleLoginButton;
